import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { flowBillingApi } from '../../services/admin/flowBillingApi'
import type {
  FlowBillingConfigInput,
  FlowBillingRecipientInput,
  PreviewParams,
} from '../../types/flowBilling'

const CONFIG_KEY = ['admin', 'flow-billing', 'config']
const RECIPIENTS_KEY = ['admin', 'flow-billing', 'recipients']
const RUNS_KEY = ['admin', 'flow-billing', 'runs']
const MISSING_MONTHS_KEY = ['admin', 'flow-billing', 'missing-months']
/**
 * Under RUNS_KEY on purpose: the preview reports whether its month has been
 * generated, so everything that invalidates the run list (generate, publish,
 * delete, backfill) must refresh it too, and the prefix does that for free.
 */
const PREVIEW_KEY = [...RUNS_KEY, 'preview']
/**
 * Under RUNS_KEY for the same reason: generating or sending a run can be what
 * takes the due period from "not yet sent" to done.
 */
const SCHEDULE_STATUS_KEY = [...RUNS_KEY, 'schedule-status']

/**
 * The server is the authority on validation, and it answers with a field-level
 * message. Surfacing that verbatim matters more here than elsewhere: "Enable at
 * least one recipient, or turn the report off" tells an admin what to do, while
 * a generic "Failed to save" leaves them guessing which of a dozen fields the
 * server objected to.
 */
const errMessage = (error: any, fallback: string) =>
  error?.response?.data?.error?.message || error?.message || fallback

export const useFlowBillingConfig = () =>
  useQuery({ queryKey: CONFIG_KEY, queryFn: flowBillingApi.getConfig })

export const useFlowBillingScheduleStatus = () =>
  useQuery({ queryKey: SCHEDULE_STATUS_KEY, queryFn: flowBillingApi.getScheduleStatus })

export const useUpdateFlowBillingConfig = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: FlowBillingConfigInput) => flowBillingApi.updateConfig(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CONFIG_KEY })
      // Group, unit and precision all change what the preview shows.
      queryClient.invalidateQueries({ queryKey: PREVIEW_KEY })
      // The schedule, and the switch, change what is due.
      queryClient.invalidateQueries({ queryKey: SCHEDULE_STATUS_KEY })
      toast.success('Settings saved')
    },
    onError: (error: any) => toast.error(errMessage(error, 'Failed to save settings')),
  })
}

export const useFlowBillingRecipients = () =>
  useQuery({ queryKey: RECIPIENTS_KEY, queryFn: flowBillingApi.listRecipients })

export const useCreateFlowBillingRecipient = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: FlowBillingRecipientInput) => flowBillingApi.createRecipient(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: RECIPIENTS_KEY })
      // The config query too: enabling depends on there being a recipient, so
      // adding one can change whether the enable switch is allowed.
      queryClient.invalidateQueries({ queryKey: CONFIG_KEY })
      toast.success('Recipient added')
    },
    onError: (error: any) => toast.error(errMessage(error, 'Failed to add recipient')),
  })
}

export const useUpdateFlowBillingRecipient = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, input }: { id: number; input: Partial<FlowBillingRecipientInput> }) =>
      flowBillingApi.updateRecipient(id, input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: RECIPIENTS_KEY }),
    onError: (error: any) => toast.error(errMessage(error, 'Failed to update recipient')),
  })
}

export const useDeleteFlowBillingRecipient = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => flowBillingApi.deleteRecipient(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: RECIPIENTS_KEY })
      queryClient.invalidateQueries({ queryKey: CONFIG_KEY })
      toast.success('Recipient removed')
    },
    onError: (error: any) => toast.error(errMessage(error, 'Failed to remove recipient')),
  })
}

export const useFlowBillingRuns = (
  params: Parameters<typeof flowBillingApi.listRuns>[0],
  enabled = true
) =>
  useQuery({
    queryKey: [...RUNS_KEY, params],
    queryFn: () => flowBillingApi.listRuns(params),
    enabled,
  })

/**
 * A stored version's report. A frozen run never changes, so it is never
 * refetched; publishing or deleting changes the run's status, not its figures.
 */
export const useRunReport = (runId: number | null) =>
  useQuery({
    queryKey: ['admin', 'flow-billing', 'run-report', runId],
    queryFn: () => flowBillingApi.getRunReport(runId as number),
    enabled: runId !== null,
    staleTime: Infinity,
  })

/**
 * The report for one month as it would be produced now.
 *
 * A query rather than a mutation so that stepping between months loads each
 * one, and stepping back is instant. Every load runs the full totals for the
 * group, so it is not refetched on window focus.
 */
export const useReportPreview = (
  period: { year: number; month: number } | null,
  excludeSerials: number[] = []
) =>
  useQuery({
    queryKey: [...PREVIEW_KEY, period, excludeSerials],
    queryFn: () => flowBillingApi.preview({ ...(period ?? {}), excludeSerials }),
    enabled: period !== null,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    placeholderData: (previous) => previous,
  })

export const useDownloadPreview = () =>
  useMutation({
    mutationFn: ({
      format,
      ...params
    }: PreviewParams & { format: 'csv' | 'pdf' }) =>
      flowBillingApi.downloadPreview(params, format),
    onError: (error: any) => toast.error(errMessage(error, 'Download failed')),
  })

export const useTestSendFlowBilling = () =>
  useMutation({
    mutationFn: (params: PreviewParams & { to?: string }) => flowBillingApi.testSend(params),
    onSuccess: (result) =>
      toast.success(`Test report sent to ${result.sentTo} — ${result.period}, ${result.rowCount} rows`),
    onError: (error: any) => toast.error(errMessage(error, 'Test send failed')),
  })

export const useDownloadRun = () =>
  useMutation({
    mutationFn: ({ runId, format }: { runId: number; format: 'csv' | 'pdf' }) =>
      format === 'csv' ? flowBillingApi.downloadRunCsv(runId) : flowBillingApi.downloadRunPdf(runId),
    onError: (error: any) => toast.error(errMessage(error, 'Download failed')),
  })

export const useSendRun = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ runId, reason }: { runId: number; reason: string | null }) =>
      flowBillingApi.sendRun(runId, reason),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: RUNS_KEY })
      const who = `${result.recipients.length} recipient${result.recipients.length === 1 ? '' : 's'}`
      toast.success(result.isRevision ? `Sent to ${who} as a revision` : `Sent to ${who}`)
    },
    onError: (error: any) => toast.error(errMessage(error, 'Send failed')),
  })
}

export const useResendRun = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (runId: number) => flowBillingApi.resendRun(runId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: RUNS_KEY })
      toast.success('Report re-sent to the current recipient list')
    },
    onError: (error: any) => toast.error(errMessage(error, 'Re-send failed')),
  })
}

/**
 * Publish a generated run.
 *
 * Invalidates the run list because publishing changes two rows, not one: the
 * run being published and whatever it supersedes.
 */
export const useUnpublishRun = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (runId: number) => flowBillingApi.unpublishRun(runId),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: RUNS_KEY })
      toast.success(
        result.restoredId
          ? 'Withdrawn — the group sees the previous version of that period again'
          : 'Withdrawn — that period no longer appears in the portal'
      )
    },
    onError: (error: any) => toast.error(errMessage(error, 'Unpublish failed')),
  })
}

export const useDeleteRun = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ runId, withdraw }: { runId: number; withdraw?: boolean }) =>
      flowBillingApi.deleteRun(runId, withdraw === true),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: RUNS_KEY })
      // The backfill scan counts a period as done if it has any run that is not
      // failed, so deleting one can put a month back on the list.
      queryClient.invalidateQueries({ queryKey: MISSING_MONTHS_KEY })
      toast.success('Marked as deleted — it is kept, and can be restored')
    },
    onError: (error: any) => toast.error(errMessage(error, 'Delete failed')),
  })
}

export const useRestoreRun = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (runId: number) => flowBillingApi.restoreRun(runId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: RUNS_KEY })
      queryClient.invalidateQueries({ queryKey: MISSING_MONTHS_KEY })
      toast.success('Restored as a working copy — publish it to hand it back to the group')
    },
    onError: (error: any) => toast.error(errMessage(error, 'Restore failed')),
  })
}

export const usePublishRun = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (runId: number) => flowBillingApi.publishRun(runId),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: RUNS_KEY })
      toast.success(
        result.supersededIds.length > 0
          ? 'Published — this is now the report the group sees for that period'
          : 'Published — the group can now see this report'
      )
    },
    onError: (error: any) => toast.error(errMessage(error, 'Publish failed')),
  })
}

/** Closed months with flow readings and no report. Drives the backfill button. */
export const useMissingMonths = (enabled = true) =>
  useQuery({
    queryKey: MISSING_MONTHS_KEY,
    queryFn: flowBillingApi.missingMonths,
    enabled,
    // The scan reads every flow reading for the group, so it is not something
    // to repeat on every window focus.
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  })

export const useBackfill = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (params: { limit?: number } = {}) => flowBillingApi.backfill(params),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: RUNS_KEY })
      queryClient.invalidateQueries({ queryKey: MISSING_MONTHS_KEY })

      if (result.generated.length === 0) {
        toast.info(
          result.skipped.length > 0
            ? `Nothing generated — ${result.skipped.length} month(s) skipped`
            : 'Nothing to backfill'
        )
        return
      }

      const tail = result.remaining > 0 ? ` · ${result.remaining} still to do` : ''
      toast.success(`Backfilled ${result.generated.length} month(s)${tail}`)

      // Skipped months are named rather than counted: "March 2026 produced no
      // rows" is actionable, "1 skipped" is not.
      if (result.skipped.length > 0) {
        toast.warning(
          `Skipped ${result.skipped.map((s) => `${s.period} (${s.reason})`).join(', ')}`
        )
      }
    },
    onError: (error: any) => toast.error(errMessage(error, 'Backfill failed')),
  })
}

export const useRunNow = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (params: PreviewParams & { reason?: string | null; sendEmail: boolean }) =>
      flowBillingApi.run(params),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: RUNS_KEY })
      const figures = `${result.rowCount} rows · ${result.totalVolume} ${result.unit}`
      // Say plainly whether anyone was emailed. The difference between a test
      // and a real invoice is the whole point of the two buttons.
      toast.success(
        result.sent
          ? `${result.period} generated and sent${result.isRevision ? ' as a revision' : ''} — ${figures}`
          : `${result.period} generated, nothing sent — ${figures}`
      )
    },
    onError: (error: any) => toast.error(errMessage(error, 'Run failed')),
  })
}
